import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../components/loader";
import { useAuth } from "./useAuth";
import { UserContext } from "../context/UserContext";
import {
  FaEnvelope,
  FaLock,
  FaGoogle,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";

import PasswordRecoveryPopup from "./PasswordRecoverPopup";
import AccountNotFoundDialog from "./AccountNotFoundDialog";
import "./StudentLogin.css";

function StudentLogin({ onClose, onShowSignup }) {
  const [showRecover, setShowRecover] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [responseMsg, setResponseMsg] = useState("");
  const [fullScreenLoading, setFullScreenLoading] = useState(false);
  const [showAccountNotFound, setShowAccountNotFound] = useState(false);

  const isDarkMode = document.body.classList.contains("dark-mode");
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const { setUserType } = useContext(UserContext);
  const formRef = useRef(null);

  useEffect(() => {
    const typedKey = "oauthLoginMessage_student";
    const fallbackKey = "oauthLoginMessage";
    const oauthMessage =
      sessionStorage.getItem(typedKey) || sessionStorage.getItem(fallbackKey);
    if (oauthMessage) {
      setResponseMsg(oauthMessage);
      sessionStorage.removeItem(typedKey);
      sessionStorage.removeItem(fallbackKey);
    }
  }, []);

  const getPostLoginRedirect = () => {
    const storedRedirect =
      sessionStorage.getItem("postLoginRedirect") ||
      localStorage.getItem("postLoginRedirect");
    if (storedRedirect) return storedRedirect;

    const from = location.state?.from;
    if (typeof from === "string") return from;
    if (from?.pathname) {
      return `${from.pathname}${from.search || ""}${from.hash || ""}`;
    }

    return null;
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const closeAll = () => {
    setShowRecover(false);
    onClose && onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponseMsg("");
    setLoading(true);
    setFullScreenLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/student/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
          credentials: "include",
        }
      );

      const data = await response.json();

      if (response.ok) {
        setUserType("student");
        await refresh("student");
        localStorage.setItem("isLoggedIn", "true");
        window.dispatchEvent(new Event("auth-changed"));
        setResponseMsg("Login successful!");

        setTimeout(() => {
          setFullScreenLoading(false);
          closeAll();
          const pending = localStorage.getItem("postAuthAction");
          // PRIORITIZE ChatBot action (pending) over stale protected-route redirect
          const redirectTo = pending || getPostLoginRedirect() || "/dashboard";
          navigate(redirectTo, { replace: true });
          localStorage.removeItem("postAuthAction");
          sessionStorage.removeItem("postLoginRedirect");
          localStorage.removeItem("postLoginRedirect");
        }, 5000);
      } else {
        if (response.status === 404 || data.code === "ACCOUNT_NOT_FOUND") {
          setShowAccountNotFound(true);
        } else {
          setResponseMsg(data.message || "Login failed!");
        }
        setFullScreenLoading(false);
      }
    } catch (err) {
      setResponseMsg("Something went wrong!");
      setFullScreenLoading(false);
    }

    setLoading(false);
  };

  const handleGoogleLogin = () => {
    setFullScreenLoading(true);
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/student/google/login`;
  };

  return (
    <>
      {fullScreenLoading && (
        <div className="fullscreen-loader-wrapper">
          <Loader />
        </div>
      )}

      {!showRecover && (
        <div
          className={`login-card ${fullScreenLoading ? "content-blur" : ""}`}
          ref={formRef}
        >
          <div className="login-header">
            <div className="auth-brand">
              <img src="/image/trans-logo.png" alt="MarsMate" className="auth-brand-icon" />
              <span className="auth-brand-text">MARSMATE AI</span>
            </div>
            <h2 className="login-title">Student Login</h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="login-input-group">
              <FaEnvelope className="login-input-icon" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                required
              />
            </div>

            <div className="login-input-group">
              <FaLock className="login-input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Password"
                required
              />
              <span
                className="login-toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEye /> : <FaEyeSlash />}
              </span>
            </div>

            <div
              className="login-recover-link"
              onClick={() => setShowRecover(true)}
            >
              Change Password
            </div>

            <button className="auth-login-btn" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </button>

            <p className="login-response">{responseMsg}</p>
          </form>

          <div className="login-divider">or</div>

          <div className="login-social">
            <button
              type="button"
              className="login-google-btn"
              onClick={handleGoogleLogin}
            >
              <FaGoogle />
            </button>
          </div>

          <div className="login-footer">
            <span>Don't have an account? </span>
            <span
              className="login-footer-link"
              onClick={onShowSignup}
            >
              Sign Up
            </span>
          </div>
        </div>
      )}

      {showRecover && (
        <PasswordRecoveryPopup
          embedded
          onCloseAll={() => setShowRecover(false)}
        />
      )}

      <AccountNotFoundDialog
        isOpen={showAccountNotFound}
        onClose={() => setShowAccountNotFound(false)}
        onConfirm={() => {
          setShowAccountNotFound(false);
          onShowSignup();
        }}
      />
    </>
  );
}

export default StudentLogin;
